// File: src/pages/BookmarkGroupPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { fetchBookmarksForGroup, getBookmarkGroup } from "../api";
import { useParams, useNavigate } from "react-router-dom";
import EditBookmarkModal from "../components/EditBookmarkModal";
import { MdBookmarkAdd } from "react-icons/md";

/**
 * Practice view for a single bookmark group with bookmark modal support
 * Route: /bookmarks/:groupId
 */
export default function BookmarkGroupPage({ user }) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const uid = user?.uid;

  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [group, setGroup] = useState(null);

  // ephemeral answers map: bookmarkId -> { value, checked, correct }
  const [answers, setAnswers] = useState({});

  // bookmark modal state
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [modalQuestion, setModalQuestion] = useState(null); // question object passed to modal

  // reload flag to refresh bookmarks from server if needed
  const reloadRef = useRef(0);

  // fetch bookmarks for this group
  useEffect(() => {
    if (!uid) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const b = await fetchBookmarksForGroup(uid, groupId);
        if (!mounted) return;
        // sort by qno or savedAt for stable order
        const sorted = (b || []).slice().sort((x, y) => {
          if (x.savedAt && y.savedAt) {
            const xa = x.savedAt.seconds
              ? x.savedAt.seconds * 1000
              : new Date(x.savedAt).getTime();
            const ya = y.savedAt.seconds
              ? y.savedAt.seconds * 1000
              : new Date(y.savedAt).getTime();
            return ya - xa;
          }
          return 0;
        });
        setBookmarks(sorted || []);
        setCurrentIndex(0);
        const groupDetails = await getBookmarkGroup(uid, groupId);
        setGroup(groupDetails);
      } catch (e) {
        console.error("Failed to fetch bookmarks for group", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
    // eslint-disable-next-line
  }, [uid, groupId, reloadRef.current]);

  // palette scroll into view
  useEffect(() => {
    const el = document.getElementById(`bookmark-q-${currentIndex}`);
    el?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  }, [currentIndex]);

  // keyboard shortcuts
  useEffect(() => {
    const total = bookmarks.length;
    function handler(e) {
      if (e.key === "ArrowRight")
        setCurrentIndex((i) => Math.min(i + 1, total - 1));
      if (e.key === "ArrowLeft") setCurrentIndex((i) => Math.max(i - 1, 0));
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [bookmarks.length]);

  function updateAnswer(bId, patch) {
    setAnswers((prev) => ({
      ...prev,
      [bId]: { ...(prev[bId] || {}), ...patch },
    }));
  }

  function handleSelectOption(bookmark, opt) {
    const key = bookmark.id;
    if (bookmark.questionType === "MMCQ") {
      const cur = (answers[key]?.value || "").toString();
      const set = new Set(cur.split("").filter(Boolean));
      if (set.has(opt)) set.delete(opt);
      else set.add(opt);
      const val = Array.from(set).sort().join("") || null;
      updateAnswer(key, { value: val, checked: false });
    } else {
      const cur = (answers[key]?.value || "").toString();
      const val = cur === opt ? null : opt;
      updateAnswer(key, { value: val, checked: false });
    }
  }

  function handleNumericChange(bookmark, val) {
    updateAnswer(bookmark.id, { value: val, checked: false });
  }

  function checkAnswer(bookmark) {
    const key = bookmark.id;
    const userVal = answers[key]?.value ?? null;
    const correctKey = bookmark.answerKey;
    let correct = false;

    if (correctKey == null) correct = false;
    else if (bookmark.questionType === "SMCQ") {
      correct = String(userVal) === String(correctKey);
    } else if (bookmark.questionType === "MMCQ") {
      const a = (userVal || "").toString().split("").sort().join("");
      const b = (correctKey || "").toString().split("").sort().join("");
      correct = a === b && a !== "";
    } else if (bookmark.questionType === "NUMERICAL") {
      const g = parseFloat(String(userVal || "").trim());
      const c = parseFloat(String(correctKey || "").trim());
      if (Number.isFinite(g) && Number.isFinite(c)) {
        const tol = Math.abs(c) * 0.01 + 0.001;
        correct = Math.abs(g - c) <= tol;
      } else {
        correct = false;
      }
    } else {
      correct = String(userVal) === String(correctKey);
    }

    updateAnswer(key, { checked: true, correct });
  }

  if (!user) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl">Please sign in to view bookmarks</h2>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6">Loading bookmarks…</div>;
  }

  if (!bookmarks || bookmarks.length === 0) {
    return (
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-xl font-semibold">Bookmarks</h1>
          <button
            onClick={() => navigate("/bookmarks")}
            className="px-2 py-1 border rounded text-sm"
          >
            Back
          </button>
        </div>
        <div className="text-gray-500">No bookmarks found in this group.</div>
      </div>
    );
  }

  const current = bookmarks[currentIndex];
  const total = bookmarks.length;

  // Handler for modal open
  function openBookmarkModalFor(b) {
    // pass the question shaped object expected by modal
    setModalQuestion({
      chapter: b.chapter,
      assignment: b.assignment,
      qno: b.qno,
      questionImage: b.questionImage,
      answerKey: b.answerKey,
      questionType: b.questionType,
      answerKeyCandidates: b.answerKeyCandidates,
      isSolutionAvailable: b.isSolutionAvailable,
      solutionImage: b.solutionImage,
      testId: b.savedFromTestId || null,
    });
    setShowBookmarkModal(true);
  }

  // onSaved callback receives various payloads from EditBookmarkModal (see modal implementation)
  async function handleBookmarkModalSaved(payload) {
    // payload can be:
    // - { action: 'deleted-from-group', groupId } OR
    // - { action: 'saved-to-group', groupId, bookmark } OR
    // - array of { groupId, res } from multi-save
    // - { action: 'created-group-and-saved', group, bookmark }
    try {
      if (!payload) return;
      // If deleted from a group and that group is the current groupId, remove the bookmark from local list
      if (payload.action === "deleted-from-group") {
        const gid = payload.groupId;
        if (gid === groupId) {
          // remove the question with same qno/assignment/chapter or id (bookmarkId)
          setBookmarks((prev) =>
            prev.filter((b) => {
              // bookmarkId deterministic: chapter__assignment__qno
              // but we can compare id as well
              const bookmarkId = `${b.chapter}__${b.assignment}__${b.qno}`;
              // If payload may include bookmark id, use it, else compare by chapter/assignment/qno
              if (payload.bookmarkId && b.id === payload.bookmarkId)
                return false;
              return !(
                b.chapter === modalQuestion.chapter &&
                b.assignment === modalQuestion.assignment &&
                String(b.qno) === String(modalQuestion.qno)
              );
            })
          );
          // adjust currentIndex safely
          setCurrentIndex((idx) =>
            Math.max(0, Math.min(idx, Math.max(0, bookmarks.length - 2)))
          );
        }
      } else if (Array.isArray(payload)) {
        // array of saves: add/update bookmark docs if any belong to this group
        // payload item structure: { groupId, res }
        for (const item of payload) {
          if (item.groupId === groupId && item.res) {
            setBookmarks((prev) => {
              const exists = prev.find((p) => p.id === item.res.id);
              if (exists) {
                return prev.map((p) => (p.id === item.res.id ? item.res : p));
              } else {
                // insert near current index or at end
                return [...prev, item.res];
              }
            });
          }
        }
      } else if (
        payload.action === "saved-to-group" ||
        payload.action === "created-group-and-saved"
      ) {
        const gid = payload.groupId || (payload.group && payload.group.id);
        const bk = payload.bookmark || payload.res;
        // If saved to current group, ensure it exists in list
        if (gid === groupId && bk) {
          setBookmarks((prev) => {
            const exists = prev.find((p) => p.id === bk.id);
            if (exists) return prev.map((p) => (p.id === bk.id ? bk : p));
            return [...prev, bk];
          });
        }
      } else {
        // fallback: if modal saved something with group matching current group, reload list.
        // We use reloadRef to trigger the fetch useEffect
        reloadRef.current += 1;
      }
    } catch (e) {
      console.error("Error handling bookmark modal saved payload", e);
    } finally {
      // optionally refresh from server to be safe
      // reloadRef.current += 1;
    }
  }

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          {group?.name || "Bookmark Group"}
        </h1>
        <div className="flex items-center gap-2">
          <div className="text-sm text-gray-600">Questions: {total}</div>
          <button
            onClick={() => navigate("/bookmarks")}
            className="px-2 py-1 border rounded text-sm"
          >
            Back
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="flex-1">
          <div className="border rounded p-3">
            <div className="mb-2 flex justify-between items-center">
              <div>
                Question {currentIndex + 1} / {total}
              </div>
              <div className="flex items-center gap-2">
                {/* Bookmark icon opens modal for current question */}
                <MdBookmarkAdd
                  className="text-xl text-green-600 cursor-pointer"
                  onClick={() => openBookmarkModalFor(current)}
                />
              </div>
            </div>

            <div className="mb-4">
              <img
                src={current.questionImage}
                alt={`Q ${currentIndex + 1}`}
                className="max-h-[520px] w-full object-contain border"
              />
            </div>

            {current.questionType === "NUMERICAL" ? (
              <div>
                <div className="mb-2">Enter numeric answer</div>
                <input
                  type="text"
                  value={answers[current.id]?.value ?? ""}
                  onChange={(e) => handleNumericChange(current, e.target.value)}
                  className="border px-3 py-2 rounded w-48"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {["A", "B", "C", "D"].map((opt) => {
                  const chosen = (answers[current.id]?.value || "").toString();
                  const isSelected =
                    current.questionType === "MMCQ"
                      ? chosen.includes(opt)
                      : chosen === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => handleSelectOption(current, opt)}
                      className={`p-3 border rounded flex justify-between items-center active:scale-[.98] ${
                        isSelected
                          ? "bg-blue-100 hover:bg-blue-200"
                          : "hover:bg-slate-100"
                      }`}
                    >
                      <div className="font-medium">{opt}</div>
                      <div className="text-sm">
                        {isSelected ? "Selected" : ""}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-4 flex items-center gap-2">
              <button
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                className="px-3 py-2 border rounded"
              >
                Prev
              </button>
              <button
                onClick={() =>
                  setCurrentIndex((i) => Math.min(total - 1, i + 1))
                }
                className="px-3 py-2 border rounded"
              >
                Next
              </button>

              <button
                onClick={() => checkAnswer(current)}
                className="ml-auto px-4 py-2 bg-blue-600 text-white rounded"
              >
                Check answer
              </button>
            </div>

            {answers[current.id]?.checked && (
              <div
                className={`mt-3 p-3 rounded ${
                  answers[current.id].correct
                    ? "bg-green-50 text-green-800"
                    : "bg-red-50 text-red-800"
                }`}
              >
                {answers[current.id].correct
                  ? "Correct"
                  : `Incorrect — correct: ${current.answerKey ?? "—"}`}
              </div>
            )}

            {/* solution only shown after checking */}
            {answers[current.id]?.checked &&
              current.isSolutionAvailable &&
              current.solutionImage && (
                <div className="mt-4">
                  <div className="text-sm font-medium">Solution</div>
                  <img
                    src={current.solutionImage}
                    alt="solution"
                    className="w-full object-contain border mt-2 max-h-56"
                  />
                </div>
              )}
          </div>
        </div>

        <div style={{ width: 260 }}>
          <div className="mb-3">
            <div className="text-sm font-medium mb-2">Question palette</div>
            <div className="grid grid-cols-5 gap-2 mt-2 overflow-auto max-h-80 border rounded p-2">
              {bookmarks.map((q, idx) => {
                const st = answers[q.id];
                let cls = "bg-gray-200";
                if (st) {
                  if (st.checked)
                    cls = st.correct ? "bg-green-400" : "bg-red-400";
                  else if (st.value) cls = "bg-green-200";
                }
                const active = currentIndex === idx;
                return (
                  <div
                    key={q.id}
                    id={`bookmark-q-${idx}`}
                    onClick={() => setCurrentIndex(idx)}
                    className={`cursor-pointer p-2 text-center rounded ${cls} transition ${
                      active ? "ring-2 ring-offset-2 ring-blue-500" : ""
                    }`}
                    title={`Q ${idx + 1}`}
                  >
                    {idx + 1}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="border rounded p-3">
            <div className="text-sm font-medium mb-2">Summary</div>
            <div>Total: {total}</div>
            <div>
              Attempted:{" "}
              {Object.values(answers).filter((a) => a && a.value).length}
            </div>
            <div>
              Checked:{" "}
              {Object.values(answers).filter((a) => a && a.checked).length}
            </div>
          </div>
        </div>
      </div>

      {showBookmarkModal && modalQuestion && (
        <EditBookmarkModal
          user={user}
          question={modalQuestion}
          onClose={() => {
            setShowBookmarkModal(false);
            setModalQuestion(null);
          }}
          onSaved={handleBookmarkModalSaved}
        />
      )}
    </div>
  );
}
